import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Dialog } from "@progress/kendo-react-dialogs";
import { Button } from "@progress/kendo-react-buttons";
import { Form, Field, FormElement } from "@progress/kendo-react-form";
import { Typography } from "@progress/kendo-react-common";
import { FaCheck } from "react-icons/fa";
import IconComponent from "../../../components/common/IconComponent";
import FormNumericTextField from "../../../components/formFields/FormNumericTextField";
import FormSelectionField from "../../../components/formFields/FormSelectionField";
import FormMultiSelectionFiled from "../../../components/formFields/FormMultiSelectionFiled";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import { customizedAdvanceHotelSearch } from "../../hotel/services/hotel.services";
import { getAllActiveCities } from "../../city/services/city.services";
import { getAllActiveMealTypes } from "../../mealtype/services/mealtype.services";
import moment from "moment";
import { clearAdvanceHotelSearchList } from "../../hotel/hotelSlice";
import "../b2bmodule.scss";
import { requiredArrayValidator } from "../../../components/formFields/CommonValidator";

interface AdvancedHotelSearchDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (hotel: any) => void;
  destination: number | string;
  startDate: any;
  selectedDates?: string[];
  nights: number;
  initialPax?: {
    adults: number;
    rooms: number;
    aweb: number;
    cweb: number;
    cnb: number;
    infants: number;
  };
}

const AdvancedHotelSearchDialog: React.FC<Omit<AdvancedHotelSearchDialogProps, 'nights'>> = ({
  isOpen,
  onClose,
  onSelect,
  destination,
  startDate,
  selectedDates = [],
  initialPax
}) => {
  const dispatch = useAppDispatch();
  const { AdvanceHotelSearchList, loading } = useAppSelector((state) => state.hotel);
  const { MealTypeList } = useAppSelector((state) => state.mealType);
  const { CityList } = useAppSelector((state) => state.city);

  const [searchTriggered, setSearchTriggered] = useState(false);

  // Clear results on open/close
  useEffect(() => {
    if (!isOpen) {
      dispatch(clearAdvanceHotelSearchList());
      setSearchTriggered(false);
    } else {
      dispatch(getAllActiveMealTypes());
      dispatch(getAllActiveCities());
      // Also clear results when opening to ensure fresh state
      dispatch(clearAdvanceHotelSearchList());
      setSearchTriggered(false);
    }
  }, [isOpen, dispatch]);

  const cityOptions = useMemo(() =>
    CityList?.map(c => ({ label: c.CityName, value: c.ID })) || [],
    [CityList]
  );

  const mealPlanOptions = useMemo(() =>
    MealTypeList?.map(m => ({ label: m.MealType, value: m.ID })) || [],
    [MealTypeList]
  );

  const starOptions = [
    { label: "5 Star", value: 5 },
    { label: "4 Star", value: 4 },
    { label: "3 Star", value: 3 },
    { label: "2 Star", value: 2 },
    { label: "1 Star", value: 1 }
  ];

  const initialValues = useMemo(() => {
    return {
      adults: initialPax?.adults ?? 2,
      rooms: initialPax?.rooms ?? 1,
      aweb: initialPax?.aweb ?? 0,
      cweb: initialPax?.cweb ?? 0,
      cnb: initialPax?.cnb ?? 0,
      infants: initialPax?.infants ?? 0,
      cityIds: [],
      mealTypeId: 0,
      starCategory: null
    };
  }, [CityList, destination, initialPax]);

  const handleSubmit = useCallback((data: any) => {
    const formats = ["DD-MM-YYYY", "YYYY-MM-DD", "DD MMM, YYYY", "DD MMM YYYY", moment.ISO_8601];
    const payload = {
      HotelID: 0,
      CityID: data.cityIds?.[0] || 0,
      CategoryID: data.starCategory || 0,
      MealTypeID: data.mealTypeId || 0,
      FilterDate: selectedDates.length > 0 ? selectedDates.join(",") : (startDate ? moment(startDate, formats).format("YYYY-MM-DD") : ""),
    };
    dispatch(customizedAdvanceHotelSearch(payload));
    setSearchTriggered(true);
  }, [selectedDates, startDate, dispatch]);

  if (!isOpen) return null;

  return (
    <Dialog
      title={(
        <div className="d-flex align-items-center gap-2">
          <Typography.h5 className="m-0 fw-bold">Advanced Search</Typography.h5>
        </div>
      )}
      onClose={onClose}
      width={1000}
      height={750}
      className="advanced-hotel-search-dialog"
    >
      <Form
        initialValues={initialValues}
        onSubmit={handleSubmit}
        render={(formRenderProps) => (
          <FormElement className="p-3 b2b-quotation-module h-100 d-flex flex-column">
            {/* Context Header */}
            <div className="d-flex gap-4 mb-3 border-bottom pb-3">
              <div className="d-flex align-items-center gap-2">
                <span className="text-muted small fw-bold">Destinations :</span>
                <span className="fw-bold">{destination}</span>
              </div>
              <div className="d-flex align-items-center gap-2">
                <span className="text-muted small fw-bold">Dates :</span>
                <span className="fw-bold text-primary">
                  {selectedDates.length > 0
                    ? selectedDates.map(d => {
                      const formats = ["DD-MM-YYYY", "YYYY-MM-DD", "DD MMM, YYYY", "DD MMM YYYY", moment.ISO_8601];
                      const dateObj = moment(d, formats);
                      return dateObj.isValid() ? dateObj.format("DD MMM") : d;
                    }).join(", ")
                    : (startDate ? (() => {
                      const formats = ["DD-MM-YYYY", "YYYY-MM-DD", "DD MMM, YYYY", "DD MMM YYYY", moment.ISO_8601];
                      const dateObj = moment(startDate, formats);
                      return dateObj.isValid() ? dateObj.format("DD MMM") : startDate;
                    })() : "N/A")}
                </span>
              </div>
            </div>

            {/* Pax Row */}
            <div className="row g-3 mb-3 align-items-end">
              <div className="col-auto">
                <Field name="adults" label="Pax/Room" component={FormNumericTextField} width={90} min={0} />
              </div>
              <div className="col-auto">
                <Field name="rooms" label="No. of Rooms" component={FormNumericTextField} width={90} min={0} />
              </div>
              <div className="col-auto">
                <Field name="aweb" label="AwEB" component={FormNumericTextField} width={70} min={0} />
              </div>
              <div className="col-auto">
                <Field name="cweb" label="CwEB" component={FormNumericTextField} width={70} min={0} />
              </div>
              <div className="col-auto">
                <Field name="cnb" label="CNB" component={FormNumericTextField} width={70} min={0} />
              </div>
              <div className="col-auto">
                <Field name="infants" label="Infants" component={FormNumericTextField} width={70} min={0} />
              </div>
            </div>

            <div className="row flex-grow-1 overflow-hidden" style={{ minHeight: 0 }}>
              {/* Left Sidebar Filters */}
              <div className="col-3 border-right pr-3 d-flex flex-column" style={{ maxHeight: "100%" }}>
                <div className="flex-grow-1 overflow-auto pe-2 pb-3">
                  <div className="mb-3">
                    <Field
                      name="cityIds"
                      label="City Locations"
                      component={FormMultiSelectionFiled}
                      options={cityOptions}
                      placeholder="Select cities..."
                      validator={requiredArrayValidator}
                    />
                  </div>

                  <div className="mb-3">
                    <Field
                      name="mealTypeId"
                      label="Meal Plan"
                      component={FormSelectionField}
                      options={mealPlanOptions}
                      placeholder="Any"
                    // validator={requiredValidator}
                    />
                  </div>

                  <div className="mb-3">
                    <Field
                      name="starCategory"
                      label="Star Category"
                      component={FormSelectionField}
                      options={starOptions}
                      placeholder="Select stars..."
                    />
                  </div>
                </div>

                <div className="pt-3 border-top bg-white mt-auto">
                  <Button
                    themeColor="primary"
                    className="w-100 fw-bold py-2 shadow-sm"
                    onClick={formRenderProps.onSubmit}
                    disabled={loading}
                    type="submit"
                  >
                    {loading ? "Searching..." : "Search"}
                  </Button>
                </div>
              </div>

              {/* Right Content - Results */}
              <div className="col-9 pl-3 h-100 overflow-auto">
                <table className="options-table w-100 border rounded">
                  <thead>
                    <tr>
                      <th style={{ width: "35%" }}>Hotel</th>
                      <th style={{ width: "55%" }}>Detailed Rates Breakdown</th>
                      <th style={{ width: "10%" }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {AdvanceHotelSearchList.length > 0 ? (
                      AdvanceHotelSearchList.map((hotel, idx) => {
                        const adults = Number(formRenderProps.valueGetter("adults")) || 2;
                        const rooms = Number(formRenderProps.valueGetter("rooms")) || 1;
                        const aweb = Number(formRenderProps.valueGetter("aweb")) || 0;
                        const cweb = Number(formRenderProps.valueGetter("cweb")) || 0;
                        const cnb = Number(formRenderProps.valueGetter("cnb")) || 0;

                        let grandTotal = 0;
                        const resultsValid = hotel.DateWiseData && hotel.DateWiseData.length > 0;

                        const dateBreakdown = resultsValid ? hotel.DateWiseData.map((d: any) => {
                          const baseRate = Number(d.DoubleOccupancyRate) || 0;
                          const awebRate = Number(d.ExtraPersonRate) || 0;
                          const cwebRate = Number(d.ChildWithBedRate) || 0;
                          const cnbRate = Number(d.ChildWithoutBedRate) || 0;

                          // If all rates are 0, this date is effectively unavailable
                          if (baseRate === 0 && awebRate === 0 && cwebRate === 0 && cnbRate === 0) {
                            return { date: d.FromDate, error: "Rates not available", total: 0 };
                          }

                          // Calculation: (Base for adults + Extras) * rooms
                          const adultTotal = (baseRate / 2) * adults;
                          const extrasTotal = (awebRate * aweb) + (cwebRate * cweb) + (cnbRate * cnb);
                          const dayTotal = (adultTotal + extrasTotal) * rooms;

                          grandTotal += dayTotal;

                          // DYNAMIC BREAKDOWN STRING: Only show non-zero items
                          const parts = [];
                          if (adults > 0 && baseRate > 0) parts.push(`(${adults}P × ${baseRate / 2})`);
                          if (aweb > 0 && awebRate > 0) parts.push(`(${aweb}×${awebRate})`);
                          if (cweb > 0 && cwebRate > 0) parts.push(`(${cweb}×${cwebRate})`);
                          if (cnb > 0 && cnbRate > 0) parts.push(`(${cnb}×${cnbRate})`);

                          const breakDownStr = `${parts.join(" + ")} × ${rooms}R`;

                          return {
                            date: d.FromDate,
                            room: d.RoomCategory,
                            roomId: d.RoomCategoryID, // Capture IDs for auto-selection
                            meal: d.MealType,
                            mealId: d.MealTypeID,     // Capture IDs for auto-selection
                            breakdown: breakDownStr,
                            total: dayTotal,
                            rates: {
                              roomPrice: baseRate / 2,
                              awebPrice: awebRate,
                              cwebPrice: cwebRate,
                              cnbPrice: cnbRate
                            }
                          };
                        }) : [];

                        const isAllUnavailable = resultsValid && dateBreakdown.every((d: any) => d.error);

                        return (
                          <tr key={idx}>
                            <td>
                              <div className="fw-bold text-dark">{hotel.HotelName}</div>
                              <div className="text-muted small">{hotel.HotelCategory} Star</div>
                              {resultsValid && !isAllUnavailable && (
                                <div className="mt-1 badge bg-info text-white small" style={{ fontSize: "10px" }}>
                                  {dateBreakdown[0].room}
                                </div>
                              )}
                            </td>
                            <td>
                              {isAllUnavailable || !resultsValid ? (
                                <div className="text-danger small italic py-2">Rates not available for selected dates</div>
                              ) : (
                                <div className="d-flex flex-column gap-2">
                                  <div className="d-flex justify-content-between align-items-center">
                                    <div className="small text-muted">Multiplication Breakdown (Date wise)</div>
                                    <div className="fw-bold text-primary h6 m-0">
                                      Total: {grandTotal.toLocaleString()}
                                    </div>
                                  </div>
                                  <div className="border-top pt-2">
                                    {dateBreakdown.map((d: any, dIdx: number) => {
                                      // Explicitly parse DD-MM-YYYY format from API
                                      const dateObj = moment(d.date, "DD-MM-YYYY");
                                      const displayDate = dateObj.isValid() ? dateObj.format("DD MMM") : d.date;

                                      return (
                                        <div key={dIdx} className="mb-2 last-child-mb-0">
                                          {d.error ? (
                                            <div className="small text-danger">{displayDate}: {d.error}</div>
                                          ) : (
                                            <div className="d-flex flex-column">
                                              <div className="d-flex justify-content-between x-small fw-semibold text-dark">
                                                <span>{displayDate} ({d.meal}):</span>
                                                <span>{d.total.toLocaleString()}</span>
                                              </div>
                                              <div className="x-small text-muted italic" style={{ fontSize: "10px" }}>
                                                {d.breakdown}
                                              </div>
                                            </div>
                                          )}
                                        </div>
                                      );
                                    })}
                                  </div>
                                </div>
                              )}
                            </td>
                            <td className="text-center">
                              <Button
                                type="button"
                                fillMode="outline"
                                themeColor="info"
                                className="rounded-circle p-2"
                                onClick={() => {
                                  onSelect({
                                    ...hotel,
                                    ID: hotel.ID,
                                    HotelName: hotel.HotelName,
                                    HotelCategory: hotel.HotelCategory,
                                    TotalPrice: grandTotal,
                                    CalculatedBreakdown: dateBreakdown,
                                    SelectedPax: {
                                      adults: formRenderProps.valueGetter("adults"),
                                      rooms: formRenderProps.valueGetter("rooms"),
                                      aweb: formRenderProps.valueGetter("aweb"),
                                      cweb: formRenderProps.valueGetter("cweb"),
                                      cnb: formRenderProps.valueGetter("cnb"),
                                      infants: formRenderProps.valueGetter("infants")
                                    }
                                  });
                                }}
                              >
                                <IconComponent icon={FaCheck} />
                              </Button>
                            </td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td colSpan={3} className="text-center py-5 text-muted">
                          {searchTriggered ? "No hotels found matching filters." : "Apply filters and click search to find hotels."}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="mt-4 pt-3 border-top d-flex gap-2">
              <Button type="button" fillMode="outline" onClick={onClose} className="px-4">Cancel</Button>
            </div>
          </FormElement>
        )}
      />
    </Dialog>
  );
};

export default AdvancedHotelSearchDialog;
